import { Box } from "@material-ui/core"

import React from "react"

import { LineChartLayout } from "../../Charts/LineChartSection/LineChartLayout"
import ParentSize from "@visx/responsive/lib/components/ParentSize"
import FiltersCharts from "../FiltersCharts"
import { useI18n } from "providers/I18n"
import { DeltaCheckbox } from "../DetailsLayout/DeltaCheckbox"
import { ExpandedLayoutTitleWithActions } from "../DetailsLayout/ExpandLineChartDialogLayout/ExpandedLayoutTitleWithActions"
import { ChartColumn } from "../DetailsLayout/ExpandLineChartDialogLayout/ChartColumn"
import { ChartRootLayoutExpanded } from "../DetailsLayout/ExpandLineChartDialogLayout/ChartRootLayoutExpanded"
import { CloseDialogButton } from "../DetailsLayout/ExpandLineChartDialogLayout/CloseDialogButton"
import { BaseChartDialog } from "../DetailsLayout/ExpandLineChartDialogLayout/BaseChartDialog"

export type ExpandLineChartDialogRef = {
  open: (
    item: { key: string; value: any },
    idx: number,
    totalEntityName: string,
  ) => void
}

export type ExpandLineChartDialogProps = {
  netKeys: string[]
  oibiaKeys: string[]
  dataType: "gcf" | "non-gcf"
  availableEntities: string[][]
  bottomTitles: string[]
  showOI: boolean[]
  windowDimensions: { width: number; height: number }
  legendItems: string[][]
  handleChangeAvailableEntities: (idx: number, entity: string) => void
  revenueChecked: boolean[]
  selectAllChecked: boolean[]
  handleCheckAllEntities: (idx: number, items: string[]) => void
  handleRevenueCheck: (i: number) => void
  handleOiChange: (i: number) => void
}

export const ExpandLineChartDialog = React.forwardRef(function ExpandDialog(
  {
    handleOiChange,
    handleRevenueCheck,
    availableEntities,
    netKeys,
    oibiaKeys,
    bottomTitles,
    dataType,
    showOI,
    windowDimensions,
    legendItems,
    handleChangeAvailableEntities,
    selectAllChecked,
    revenueChecked,

    handleCheckAllEntities,
  }: ExpandLineChartDialogProps,
  ref,
) {
  const { t } = useI18n()

  const [open, setOpen] = React.useState(false)
  const [item, setItem] = React.useState({
    item: { key: "", value: [] },
    index: 0,
    totalEntityName: "",
  })

  const handleClose = React.useCallback(() => {
    setOpen(false)
  }, [])

  React.useImperativeHandle(
    ref,
    () => ({
      open: (
        item: { key: string; value: any },
        index: number,
        totalEntityName: string,
      ) => {
        setOpen(true)
        setItem({ item, index, totalEntityName })
      },
    }),
    [],
  )

  return (
    <BaseChartDialog
      onClose={handleClose}
      open={open}
      width={windowDimensions.width}
    >
      <Box display="flex">
        <ChartColumn>
          <ExpandedLayoutTitleWithActions
            title={
              dataType === "non-gcf"
                ? "NET REVENUE VARIANCE (%) / OIBIA VARIANCE (%)"
                : "Δ REVENUE (%) / Δ OI (%)"
            }
          >
            <Box display="flex" paddingRight="64px">
              <DeltaCheckbox
                title={
                  dataType === "non-gcf"
                    ? `${item.totalEntityName} Net Revenue Variance`
                    : `${item.totalEntityName} ${
                        t("revenue.delta.revenue") + "(%)"
                      }`
                }
                checked={revenueChecked[item.index]}
                key={item.index}
                onChange={() => handleRevenueCheck(item.index)}
              />

              <DeltaCheckbox
                title={
                  dataType === "non-gcf"
                    ? `${item.totalEntityName} OIBIA Variance`
                    : `${item.totalEntityName} Δ OI (%)`
                }
                checked={showOI[item.index]}
                key={item.index}
                onChange={() => handleOiChange(item.index)}
              />
            </Box>
          </ExpandedLayoutTitleWithActions>
          <ChartRootLayoutExpanded>
            <ParentSize debounceTime={10}>
              {({ width: visWidth, height: visHeight }) => (
                <LineChartLayout
                  item={item.item}
                  width={!!visWidth ? visWidth - 64 : 0}
                  height={visHeight}
                  bottomTitles={bottomTitles}
                  netKeys={netKeys}
                  oibiaKeys={oibiaKeys}
                  dataType={dataType}
                  availableEntities={availableEntities}
                  entityIndex={item.index}
                  showOI={showOI[item.index]}
                  showRevenue={revenueChecked[item.index]}
                />
              )}
            </ParentSize>
          </ChartRootLayoutExpanded>{" "}
        </ChartColumn>

        <Box display="flex" flexDirection="column">
          <Box display="flex" justifyContent="flex-end" marginBottom="42px">
            <CloseDialogButton onClick={() => setOpen(false)} />
          </Box>
          <FiltersCharts
            itemKey={item.item.key}
            legendItems={legendItems[item.index]}
            itemIndex={item.index}
            availableEntities={availableEntities}
            handleChangeAvailableEntities={handleChangeAvailableEntities}
            isOpen={true}
            handleOpen={() => null}
            isExpanded
            handleCheckAllEntities={handleCheckAllEntities}
            checked={selectAllChecked[item.index]}
          />
        </Box>
      </Box>
    </BaseChartDialog>
  )
})
